import { logSwallowed } from '@/lib/errors';
import { useState, useEffect } from 'react';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { useAuthStore } from '@/store/authStore';
import { goLauncher } from '@/lib/navigate';
import { decodeJwtPayload } from '@/lib/jwt';
import { deviceLoginInfo } from '@/lib/deviceIdentity';
import { useAccessGateStore } from '@/lib/accessGate';

export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const centerToken = useCenterStore.getState().token;
      const authUser = useAuthStore.getState().user;
      if (centerToken && authUser) {
        setUser(authUser);
      }
    } catch {
      // Not logged in
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (loginStr: string, password?: string, pin?: string) => {
    const sdk = getSDK();
    console.log('[useAuth] calling sdk.auth.login', { login: loginStr });
    let result: any;
    try {
      result = await sdk.auth.login({
        login: loginStr,
        password: password || '',
        pin: pin || '',
        ...deviceLoginInfo(),
      });
    } catch (err) {
      console.error('[useAuth] sdk.auth.login FAILED:', err);
      throw err;
    }
    console.log('[useAuth] login result:', result);
    const token = result.token || '';
    const entity = result.entity || { id: loginStr, name: loginStr };
    // Decode roles + permissions from JWT (единый base64url-декод — atob на
    // base64url бросал исключение и роли молча терялись).
    // ВАЖНО: по умолчанию — ПУСТО (раньше стояло ['Администратор'], из-за чего
    // при отсутствии roles в JWT КАЖДЫЙ пользователь считался админом — в доке
    // появлялись «Файлы» и т.п.).
    let roles: string[] = [];
    let permissions: string[] = [];
    const payload = decodeJwtPayload(token);
    if (payload) {
      if (Array.isArray(payload.roles)) {
        roles = payload.roles.map(String);
      }
      if (Array.isArray(payload.permissions)) {
        permissions = payload.permissions.map(String);
      }
    }

    // Сохраняем в centerStore (SDK, токен)
    useCenterStore.getState().login(token, {
      id: entity.id,
      login: loginStr,
      name: entity.name || loginStr,
    });

    // Сохраняем в authStore (ProtectedRoute проверяет isAuthenticated)
    useAuthStore.getState().setToken(token);
    useAuthStore.getState().setUser({
      id: entity.id,
      name: entity.name || loginStr,
      role: 'admin',
      roles,
      permissions,
      employeeId: entity.id,
    });

    // OFFLINE-ВХОД: сессия в IndexedDB (восстановление при старте без сети)
    void import('@/lib/offlineSession').then((m) => m.saveOfflineSession({
      token,
      user: { id: entity.id, name: entity.name || loginStr, login: loginStr, roles, permissions },
    }).catch((e) => logSwallowed(e, 'api/hooks/useAuth.ts:78')));

    setUser({ id: entity.id, name: entity.name || loginStr, role: 'admin' });
    return result;
  };

  const logout = () => {
    // Отзыв сессии на сервере (best-effort: сервер недоступен — сессия
    // протухнет сама; явный выход всегда сбрасывает локальные сторы)
    try {
      getSDK().auth.logout({}).catch((e) => logSwallowed(e, 'api/hooks/useAuth.ts:88'));
    } catch (e) { logSwallowed(e, 'api/hooks/useAuth.ts:89'); }
    useCenterStore.getState().logout();
    useAuthStore.getState().logout();
    // Гейтинг доступа относится к сессии/пользователю — снимаем баннер.
    useAccessGateStore.getState().clear();
    void import('@/lib/offlineSession').then((m) => m.clearOfflineSession().catch((e) => logSwallowed(e, 'api/hooks/useAuth.ts:92')));
    setUser(null);
    // Выход — В ЛАУНЧЕР (как в 1С: стартовое окно со списком баз), не на /login.
    // Сброс сторов вызывает <Navigate to="/login"> из ProtectedRoute при
    // перерендере — навигация в лаунчер выполняется ПОСЛЕДНЕЙ (setTimeout 0),
    // иначе редирект на /login перезаписывает её.
    setTimeout(() => goLauncher(), 0);
  };

  return { user, loading, login, signIn: login, signOut: logout, logout };
}
